import { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { BotUserId, BotUserId2 } from '@/constants'
import { useHall } from '@/contexts/HallProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { SpeakingRole, Winner } from '@db8/shared'
import { DialogTitle } from '@radix-ui/react-dialog'
import { StartingCountdown } from './StartingCountdown'
import { usePoll } from '@/contexts/PollProvider'
import { useTeamColors } from '@/hooks/useTeamColors'
import useDarkMode from '@/hooks/useDarkMode'
import TextLoading from './ui/textLoading'
import ProfileImage from './ProfileImage'

export const WinningTeamCard = ({ user, speakingRole }: { user: any; speakingRole: Winner }) => {
  const { borderColor } = useTeamColors({ speakingRole })

  return (
    <div
      className={`flex flex-col items-center rounded-xl bg-white dark:bg-transparent shadow-md p-4 w-32 md:w-40 lg:w-48 border border-2`}
      style={{ borderColor }}
    >
      <div className="w-20 h-20 rounded-full overflow-hidden mb-3">
        <ProfileImage user={user} />
      </div>
      <h1 className="text-sm font-semibold text-center text-gray-900 dark:text-white">
        {user.displayName}
      </h1>
    </div>
  )
}

export const WinnerAnnouncement = () => {
  const { hallMetadata, hall } = useHall()
  const { getUser, affirmativeUsers, negativeUsers, hallUsers } = useHallUsers()
  const { showWinner, setShowWinner, winningTeam } = usePoll()
  const isDark = useDarkMode()
  const { bgClass } = useTeamColors({ speakingRole: hallMetadata?.winningTeam })

  const winnerUsers = useMemo(() => {
    if (hallMetadata?.winningTeam === Winner.Affirmative) return affirmativeUsers
    else if (hallMetadata?.winningTeam === Winner.Negative) return negativeUsers
    else return []
  }, [hallMetadata?.winningTeam, affirmativeUsers, negativeUsers])

  const hasLoadedUsers = useMemo(() => {
    const list =
      hallMetadata?.winningTeam === Winner.Tie
        ? [...affirmativeUsers, ...negativeUsers]
        : winnerUsers
    return list?.length > 0
  }, [affirmativeUsers, negativeUsers, winnerUsers, hallMetadata?.winningTeam])

  return (
    <Dialog
      open={showWinner.val}
      onOpenChange={(open) => {
        if (!open) setShowWinner({ val: false, initialized: true })
      }}
    >
      <DialogContent
        size="lg"
        hasCloseBtn={false}
        className={`dark:text-white text-center w-full lg:min-w-[500px] max-w-[95vw] md:max-w-[70vw] p-4 sm:p-6 pb-10 sm:pb-12 noScrollbar overflow-y-auto max-h-[90vh] min-h-[500px] rounded-lg ${
          isDark && bgClass
        }`}
      >
        <div className="w-full h-full !border-none">
          {hasLoadedUsers ? (
            <div className="flex flex-col h-full">
              <DialogTitle className="text-2xl md:text-3xl lg:text-4xl font-bold">
                {hallMetadata?.winningTeam === Winner.Tie
                  ? "It's a Tie!"
                  : `Winner Is ${winningTeam}`}
              </DialogTitle>

              <p className="py-6 text-lg font-semibold">
                {hallMetadata?.winningTeam === Winner.Tie
                  ? 'Congratulations to both teams for an excellent performance!'
                  : `Great job to everyone on ${winningTeam}`}
              </p>

              <div className="flex flex-col gap-8 md:flex-row md:items-start items-center justify-center w-full">
                {(hallMetadata?.winningTeam === Winner.Tie
                  ? [
                      { role: Winner.Affirmative, users: affirmativeUsers },
                      { role: Winner.Negative, users: negativeUsers },
                    ]
                  : [{ role: hallMetadata?.winningTeam, users: winnerUsers }]
                ).map(({ role, users }) => (
                  <div
                    key={`${role}`}
                    className="flex flex-col items-center gap-4 w-full max-w-[500px]"
                  >
                    <span className="text-lg sm:text-xl font-semibold text-center">
                      {role === SpeakingRole.Affirmative
                        ? hall.affirmingTeamName
                        : hall.dissentingTeamName}
                    </span>
                    <div className="flex flex-wrap justify-center gap-4">
                      {users.map((userId) => {
                        const user = getUser(userId)
                        return user ? (
                          <WinningTeamCard key={user.id} user={user} speakingRole={role} />
                        ) : null
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="flex items-center justify-center h-full w-full text-md mt-4 min-h-[400px]">
              <TextLoading text="Loading team members" />
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
